import type { ReactNode } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { cn } from "@/shared/lib/cn";
import { initials } from "@/shared/lib/initials";
import { AwayMarker } from "@/shared/presence/away-marker";

interface Props {
  displayName: string;
  avatarUrl?: string | null;
  size?: "xs" | "sm" | "md" | "lg";
  /// Optional className to override sizing or add chrome (rings, borders).
  /// Sizing here wins over `size` if both set a size-* utility.
  className?: string;
  /// Optional className applied to the fallback chip. Defaults to the brand
  /// soft tone used across the product (sidebar, directory, members).
  fallbackClassName?: string;
  /**
   * Optional. When the caller has one, the avatar marks the person as away
   * from the presence map.
   *
   * Callers holding only a `userId` leave it out and draw no marker.
   * `out-today` returns membership ids, and the alternative (joining through
   * a directory page that may not be loaded) would put a wrong dot on a
   * stranger, which is much worse than a missing one.
   */
  membershipId?: string;
  /// Ring colour for the away marker, which must match the surface BEHIND the
  /// avatar. Defaults to the page background.
  ringClassName?: string;
}

const SIZE_TO_CLASSES: Record<NonNullable<Props["size"]>, string> = {
  xs: "size-5 text-4xs",
  sm: "size-6 text-3xs",
  md: "size-8 text-2xs",
  lg: "size-10 text-sm",
};

/// Shared product-wide user avatar. Renders `avatarUrl` when present and
/// falls back to two-letter initials. Use this everywhere a User is shown so
/// the image-vs-initials decision is centralized (every User has a default
/// avatar assigned at creation; this component just trusts that contract).
export function UserAvatar({
  displayName,
  avatarUrl,
  size = "md",
  className,
  fallbackClassName,
  membershipId,
  ringClassName,
}: Props): ReactNode {
  return (
    <AwayMarker membershipId={membershipId} ringClassName={ringClassName}>
      <Avatar className={cn("shrink-0", SIZE_TO_CLASSES[size], className)}>
        <AvatarImage src={avatarUrl ?? undefined} alt="" />
        <AvatarFallback
          className={cn("bg-pulse-soft font-semibold text-text-brand-strong", fallbackClassName)}
        >
          {initials(displayName)}
        </AvatarFallback>
      </Avatar>
    </AwayMarker>
  );
}
